import { useNavigate } from 'react-router-dom';
import { BankAddressDto, LookupListDto, TenureUnit } from 'api/openapi';
import { format, isAfter } from 'date-fns';

export function removeTooltipInGridPagination() {
  //remove unwanted toottip in prev/next buttons
  const nav = document.querySelectorAll('.k-link.k-pager-nav');
  if (nav) {
    nav.forEach(e => {
      const title = e.getAttribute('title');
      if (title === 'Go to the next page') {
        e.classList.add('next-page-btn');
        e.setAttribute('title', '');
      } else if (title === 'Go to the previous page') {
        e.classList.add('prev-page-btn');
        e.setAttribute('title', '');
      }
    });
  }
}

//date format to dd/mm/yyyy using js only
export const convertDateToDDMMYYYY = (
  dateInput: string | Date | null | undefined,
): string | undefined => {
  if (!dateInput) return undefined;

  // Convert input to string if it's a Date object
  const dateString =
    dateInput instanceof Date ? dateInput.toISOString() : String(dateInput);

  // Handle DD/MM/YYYY or DD-MM-YYYY format
  if (dateString.match(/^\d{2}[/-]\d{2}[/-]\d{4}$/)) {
    return dateString.replace(/\//g, '-');
  }

  // Try to parse the date
  let date: Date;
  try {
    // If date is in ISO or US format
    date = new Date(dateString);

    // Check if date is valid
    if (isNaN(date.getTime())) {
      // Try parsing DD/MM/YYYY format manually
      const [day, month, year] = dateString.split(/[/-]/);
      date = new Date(Number(year), Number(month) - 1, Number(day));
    }

    // Check if the parsed date is valid
    if (isNaN(date.getTime())) {
      throw new Error('Invalid date');
    }
    const timezoneOffset: number = date.getTimezoneOffset();

    // Adjust date by adding timezone offset
    const adjustedTime: number = date.getTime() - timezoneOffset * 60 * 1000;
    const localDate = new Date(adjustedTime);

    return localDate
      .toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      })
      .replace(/\//g, '-');
  } catch (error) {
    console.error('Error converting date:', error);
    return '';
  }
};

export const getExpiryMessage = (
  dateInput: string | Date | null | undefined,
  daysThreshold = 15,
): string | null => {
  if (!dateInput) return null;

  const expiryDate = new Date(dateInput);
  if (isNaN(expiryDate.getTime())) return null;

  const currentDate = new Date();
  currentDate.setHours(0, 0, 0, 0);
  expiryDate.setHours(0, 0, 0, 0);

  const timeDifference = expiryDate.getTime() - currentDate.getTime();
  const daysRemaining = Math.ceil(timeDifference / (1000 * 60 * 60 * 24));

  if (daysRemaining > 1 && daysRemaining <= daysThreshold) {
    return `Expire in ${daysRemaining} days`;
  }

  if (daysRemaining === 1) {
    return `Expires in ${daysRemaining} day`;
  }

  if (daysRemaining === 0) {
    return 'Expire today';
  }

  if (daysRemaining < 0) {
    return `Expired on ${convertDateToDDMMYYYY(dateInput) ?? ''}`;
  }

  return null;
};

// date field value conversion
export const convertDateFieldValueToLocalTimeZone = (dateString: string) => {
  if (!dateString) return;
  const date = new Date(dateString);
  const timezoneOffset = date.getTimezoneOffset();
  const localDate = new Date(date.getTime() - timezoneOffset * 60000);
  return localDate.toISOString();
};
//This function is to convert minimum 2 decimal and add % at the end. eg: 62.22222227 -> 62.22%
export function formatUtilizationRate(
  rate: number,
  digit5Decimal?: boolean,
): string {
  if (rate % 1 === 0) {
    return `${rate}%`;
  }

  const decimalPlaces = digit5Decimal ? 5 : 4;
  return `${rate.toFixed(decimalPlaces)}%`;
}

export const facilityMinDate = (value: Date | null) => {
  if (!value) return false;
  // Check if the value is GREATER than or EQUAL to the min date (2000-01-01)
  return new Date(value) >= new Date(2000, 0, 1);
};

export const facilityMaxDate = (value: Date | null) => {
  if (!value) return false;
  // Check if the value is LESS than or EQUAL to the max date (2100-12-31)
  return new Date(value) <= new Date(2100, 11, 31);
};
export type RedirectPath = (redirectPath: string) => void;
export function useNavigateToRoute(): RedirectPath {
  const navigate = useNavigate();

  function redirect(redirectPath: string) {
    navigate(redirectPath);
  }
  return redirect;
}

export const formatDateDDM = (date: string) => {
  if (date) {
    const originalDate = new Date(date);
    // Format the original date as 'dd MMM' and convert to uppercase
    const formattedDate = format(originalDate, 'dd MMM').toUpperCase();
    return formattedDate; // Example output: "17 DEC"
  }
  return; // Return undefined if no date is provided
};

export const formatTimeTo12Hour = (dateTime: string) => {
  if (dateTime) {
    // Append 'Z' to indicate UTC
    const date = new Date(`${dateTime}Z`);
    // Extract hours and minutes in local time
    let hours = date.getHours();
    const minutes = date.getMinutes();
    const period = hours >= 12 ? 'PM' : 'AM';

    // Convert 24-hour to 12-hour format
    hours = hours % 12 || 12; // Convert "0" to "12"

    // Format the time
    return `${hours}:${minutes.toString().padStart(2, '0')} ${period}`; // Output: "5:30 AM"
  }
  return null;
};

//format date (12-12-2023) and time (12:00 AM)
export function formatDateTime(dateString: string): string {
  const date = new Date(`${dateString}Z`);
  const dateObj = new Date(date);
  const formatNumber = (num: number) => (num < 10 ? `0${num}` : num);
  // Extract components
  const day = formatNumber(dateObj.getDate());
  const month = formatNumber(dateObj.getMonth() + 1); // Months are 0-based
  const year = dateObj.getFullYear();

  let hours = dateObj.getHours();
  const minutes = formatNumber(dateObj.getMinutes());
  const period = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12 || 12; // Convert to 12-hour format

  return `${day}-${month}-${year}  ${hours}:${minutes} ${period}`;
}

export function formatAmount(amount: number): string {
  if (amount >= 1e12) {
    return `${(amount / 1e12).toFixed(2)}T`; // Trillions
  } else if (amount >= 1e9) {
    return `${(amount / 1e9).toFixed(2)}B`; // Billions
  } else if (amount >= 1e6) {
    return `${(amount / 1e6).toFixed(2)}M`; // Millions
  } else if (amount >= 1e3) {
    return `${(amount / 1e3).toFixed(2)}K`; // Thousands
  }
  return amount.toString(); // Less than 1000
}

//function to show expiry alert
export const shouldRenderExpiryAlert = (
  date: string,
  daysThreshold: number,
): boolean => {
  if (!date || isNaN(new Date(date).getTime())) {
    return false; // Handle invalid date input
  }

  const currentDate = new Date();
  const expiryDate = new Date(date);

  // Reset both dates to midnight to avoid time component affecting comparison
  currentDate.setHours(0, 0, 0, 0);
  expiryDate.setHours(0, 0, 0, 0);

  const timeDifference = expiryDate.getTime() - currentDate.getTime();
  const daysRemaining = Math.ceil(timeDifference / (1000 * 60 * 60 * 24));

  // Show the alert if the date is expired or within the threshold
  return daysRemaining <= 0 || daysRemaining <= daysThreshold;
};

//use this for filtering a lookup
export const getLookupArray = (lookup: string, lookUps: LookupListDto[]) => {
  return (
    lookUps.find(obj => {
      return obj.type === lookup;
    })?.values ?? []
  );
};

//formatAddress into a single string
export const formatAddresstoString = (address?: BankAddressDto): string => {
  const addressVal: BankAddressDto = {
    street: address?.street ?? '',
    city: address?.city ?? '',
    state: address?.state ?? '',
    zipCode: address?.zipCode ?? '',
  };

  return [
    addressVal.street,
    addressVal.city,
    addressVal.state,
    addressVal.zipCode,
  ]
    .filter(part => part) // Remove empty strings or // Filter out null or undefined values
    .join(', '); // Join the properties into a single string
};

/**
 * Checks if the provided id matches the specific id and the startDate is a future date.
 *
 * @param id - The id to be checked.
 * @returns A boolean indicating if the conditions are satisfied.
 */
export function isLoanClosed(id?: string): boolean {
  // Return true if id matches with loanClosed Status Id.
  const specificId = '27a83f3f-8b9f-4185-8f02-358ce95dbe6b'; //loanClosed Status Id from lookup
  return id === specificId;
}

export function isOverDue(id?: string): boolean {
  // Return true if id matches with loanClosed Status Id.
  const specificId = '47fa2ea0-e069-4f7f-bfd1-fde37d48741f'; //loanClosed Status Id from lookup
  return id === specificId;
}

/**
 * Checks if the given date is a future date compared to the current date.
 *
 * @param date - The date to be checked.
 * @returns A boolean indicating if the date is in the future.
 */
export function isFutureDate(date?: string | Date): boolean {
  if (!date) return false; // If no date is provided, return false
  const currentDate = new Date();
  return isAfter(new Date(date), currentDate);
}

/**
 * Checks if the first date is greater (after) than the second date.
 *
 * @param date1 - The first date to be compared.
 * @param date2 - The second date to be compared.
 * @returns A boolean indicating if date1 is greater than date2.
 */
export function isDateGreater(
  date1?: string | Date,
  date2?: string | Date,
): boolean {
  if (!date1 || !date2) return false; // If either date is missing, return false

  const firstDate = new Date(date1);
  const secondDate = new Date(date2);

  // Compare the dates
  return firstDate > secondDate;
}

export const formatDateToDateRange = (dateString?: string) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return format(date, 'MMM d, yyyy'); // "Dec 31, 2024"
};
//get timezone
export const getTimeZone = () =>
  Intl.DateTimeFormat().resolvedOptions().timeZone;

export const pascalCasingToSentance = (str: string) => {
  return (
    str
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      // Add space between numbers and letters
      .replace(/([0-9])([A-Z])/g, '$1 $2')
      .replace(/([A-Z])([0-9])/g, '$1 $2')
      // Handle consecutive uppercase letters (like "JSON")
      .replace(/([A-Z])([A-Z][a-z])/g, '$1 $2')
      // Convert to lowercase at the end
      .toLowerCase()
  );
};

export const dateToTimeConvertor = (dateString: string): string => {
  // Ensure the string has the 'Z' to represent UTC time
  if (!dateString.endsWith('Z')) {
    dateString += 'Z'; // Append 'Z' if it doesn't exist
  }

  const date = new Date(dateString);
  const now = new Date();
  const diffInMilliseconds = now.getTime() - date.getTime();

  if (isNaN(date.getTime())) {
    return 'Invalid date';
  }

  // Convert to various time units
  const seconds = Math.floor(diffInMilliseconds / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  const weeks = Math.floor(days / 7);
  const months = Math.floor(days / 30.44); // Average month length
  const years = Math.floor(days / 365.25); // Account for leap years

  if (years > 0) {
    return `${years} ${years === 1 ? 'year' : 'years'} ago`;
  } else if (months > 0) {
    return `${months} ${months === 1 ? 'month' : 'months'} ago`;
  } else if (weeks > 0) {
    return `${weeks} ${weeks === 1 ? 'week' : 'weeks'} ago`;
  } else if (days > 0) {
    return `${days} ${days === 1 ? 'day' : 'days'} ago`;
  } else if (hours > 0) {
    return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`;
  } else if (minutes > 0) {
    return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'} ago`;
  } else {
    return `${seconds} ${seconds === 1 ? 'second' : 'seconds'} ago`;
  }
};

export const transformText = (text: string): string => {
  if (!text) return text;

  const words = text.split(' ');
  if (!words.length) return text;

  const result = [words[0]];

  for (let i = 1; i < words.length; i++) {
    const word = words[i];
    if (word.length > 0 && /[A-Z]/.test(word.slice(1)) && /[a-z]/.test(word)) {
      // Convert PascalCase to space-separated lowercase
      const formatted = word
        .replace(/([A-Z])/g, ' $1')
        .trim()
        .toLowerCase();
      result.push(formatted);
    } else {
      result.push(word);
    }
  }

  return result.join(' ');
};

// Function to convert array of ISO date strings to array of date objects
export const convertDates = dateArray => {
  return dateArray?.map(isoDateString => {
    const inputDateTime = new Date(isoDateString);

    // Determine the start or end of the day based on the input timestamp
    return isoDateString.includes('T18:30:00.000Z')
      ? new Date( // Start of the day
          inputDateTime.getFullYear(),
          inputDateTime.getMonth(),
          inputDateTime.getDate(),
          0,
          0,
          0,
        )
      : new Date( // End of the day (23:59:59)
          inputDateTime.getFullYear(),
          inputDateTime.getMonth(),
          inputDateTime.getDate(),
          23,
          59,
          59,
        );
  });
};
export const formatDateRange = (dateString: string): string => {
  const date = new Date(dateString);
  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  };
  return date.toLocaleDateString('en-US', options);
};

export type TenureUnitType = 'days' | 'month' | 'year';

/**
 * Converts tenure value in days based on unit.
 * @param unit - 'day', 'month', or 'year'
 * @param value - numeric value of the tenure
 * @returns number of days as integer
 */
export function calculateDays(unit: TenureUnitType, value: number): number {
  switch (unit) {
    case 'days':
      return value;
    case 'month':
      return value % 12 === 0 ? (value / 12) * 365 : value * 30;
    case 'year':
      return value * 365;
    default:
      throw new Error(`Unsupported unit: ${unit}`);
  }
}

export const tenureUnitOptions = [
  {
    id: 'days',
    name: 'Days',
  },
  {
    id: 'month',
    name: 'Month',
  },
  {
    id: 'year',
    name: 'Year',
  },
];
export function numberToWords(num: number): string {
  if (num === 0) return 'Zero';

  const ones = [
    '',
    'One',
    'Two',
    'Three',
    'Four',
    'Five',
    'Six',
    'Seven',
    'Eight',
    'Nine',
    'Ten',
    'Eleven',
    'Twelve',
    'Thirteen',
    'Fourteen',
    'Fifteen',
    'Sixteen',
    'Seventeen',
    'Eighteen',
    'Nineteen',
  ];
  const tens = [
    '',
    '',
    'Twenty',
    'Thirty',
    'Forty',
    'Fifty',
    'Sixty',
    'Seventy',
    'Eighty',
    'Ninety',
  ];
  const digits = [
    'Zero',
    'One',
    'Two',
    'Three',
    'Four',
    'Five',
    'Six',
    'Seven',
    'Eight',
    'Nine',
  ];

  function convertHundreds(n: number): string {
    let result = '';
    if (n >= 100) {
      result += ones[Math.floor(n / 100)] + ' Hundred';
      n %= 100;
      if (n > 0) result += ' ';
    }
    if (n >= 20) {
      result += tens[Math.floor(n / 10)];
      if (n % 10 > 0) result += ' ' + ones[n % 10];
    } else if (n > 0) {
      result += ones[n];
    }
    return result;
  }

  const numStr = String(num);
  const [intPart, decPart] = numStr.split('.');
  let intNum = parseInt(intPart, 10);

  let result = '';
  if (intNum === 0) {
    result = 'Zero';
  } else {
    const groups = ['', ' Thousand', ' Million', ' Billion', ' Trillion'];
    let groupIndex = 0;
    while (intNum > 0) {
      const group = intNum % 1000;
      if (group !== 0) {
        result =
          convertHundreds(group) +
          groups[groupIndex] +
          (result ? ' ' + result : '');
      }
      intNum = Math.floor(intNum / 1000);
      groupIndex++;
    }
  }

  if (decPart && parseInt(decPart, 10) > 0) {
    result +=
      ' Point ' +
      decPart
        .split('')
        .map(d => digits[parseInt(d)])
        .join(' ');
  }

  return result.trim();
}

export function getLoanTenureLabel(
  tenureValue: number,
  tenureUnit?: TenureUnit,
): string {
  if (tenureValue <= 0 || tenureUnit === undefined || tenureUnit === null) {
    return '';
  }

  const unitMap: Record<TenureUnit, string> = {
    [TenureUnit._1]: 'Day',
    [TenureUnit._2]: 'Month',
    [TenureUnit._3]: 'Year',
  };

  let unit = unitMap[tenureUnit];

  if (tenureValue > 1) {
    unit += 's';
  }

  return `${tenureValue} ${unit}`;
}
